/**
 * StatusDashboardIcon - Single status icon for the dashboard cruscotto
 * Displays a colored circle with Material icon based on status
 */

import { Tooltip } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import styles from './StatusDashboard.module.css';

export type StatusType = 'success' | 'warning' | 'error' | 'default';

interface StatusDashboardIconProps {
  /** Material icon name */
  icon: string;
  /** Custom SVG icon (rendered instead of Material icon; inherits status color via currentColor) */
  svgIcon?: React.ReactNode;
  /** Custom text icon (rendered as bold text instead of Material icon) */
  textIcon?: string;
  /** Whether to render textIcon with strikethrough */
  textIconStrike?: boolean;
  /** Status determines the color */
  status: StatusType;
  /** Optional click handler */
  onClick?: () => void;
  /** Tooltip text shown on hover */
  tooltip?: string;
  /** Small badge rendered in the bottom-right corner (e.g. the MasterPower mark to flag the data source) */
  cornerBadge?: React.ReactNode;
}

const statusClassMap: Record<StatusType, string> = {
  success: styles.statusSuccess,
  warning: styles.statusWarning,
  error: styles.statusError,
  default: styles.statusDefault,
};

export const StatusDashboardIcon: React.FC<StatusDashboardIconProps> = ({
  icon,
  svgIcon,
  textIcon,
  textIconStrike,
  status,
  onClick,
  tooltip,
  cornerBadge,
}) => {
  const renderIcon = () => {
    if (svgIcon) {
      return svgIcon;
    }
    if (textIcon) {
      return (
        <span style={{
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: -0.5,
          lineHeight: 1,
          textDecoration: textIconStrike ? 'line-through' : undefined,
        }}>
          {textIcon}
        </span>
      );
    }
    return <MaterialIcon name={icon} size={20} />;
  };

  const iconElement = (
    <div
      className={`${styles.iconCircle} ${statusClassMap[status]}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
    >
      {renderIcon()}
      {cornerBadge && <span className={styles.cornerBadge}>{cornerBadge}</span>}
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="top">
        {iconElement}
      </Tooltip>
    );
  }

  return iconElement;
};
